import React, { useState } from "react";
import { ShareNetwork, CaretRight, Copy, CheckCircle, X, ChatCircle, EnvelopeSimple, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const APP_URL = "https://tallycarbcounter.com";
const APP_PITCH =
  "Snap a photo of your plate and Tally will tell you the carbs in seconds. Free to try — works on iPhone & Android, no app store needed.";
const SHARE_MESSAGE = `${APP_PITCH}\n\n${APP_URL}`;

export default function ShareButton() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleNativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: "Tally · Carb Counter",
          text: APP_PITCH,
          url: APP_URL,
        });
        // success — close the modal
        setOpen(false);
      } catch (err) {
        // User cancelled, no-op
      }
    } else {
      // No native share — fallback to copy
      handleCopy();
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_MESSAGE);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error("Couldn't copy — long-press to select the link below");
    }
  };

  const handleSMS = () => {
    window.location.href = `sms:?&body=${encodeURIComponent(SHARE_MESSAGE)}`;
  };

  const handleEmail = () => {
    const subject = encodeURIComponent("You should try Tally · Carb Counter");
    const body = encodeURIComponent(SHARE_MESSAGE);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 border-t border-[#E6E3D8] hover:bg-[#F0EFE9] transition-colors text-left"
        data-testid="menu-share"
      >
        <div className="flex items-center gap-3">
          <ShareNetwork size={18} className="text-[#5C6B62]" />
          <span className="text-sm text-[#1A2421]">Share Tally with a friend</span>
        </div>
        <CaretRight size={14} className="text-[#5C6B62]" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#1A2421]/45 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 pb-4"
          data-testid="share-modal"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-[#F9F8F6] p-6 sm:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-[#5C6B62] hover:text-[#1A2421]"
              aria-label="Close"
              data-testid="share-modal-close"
            >
              <X size={18} />
            </button>

            <div className="flex justify-center pt-2">
              <span className="w-14 h-14 rounded-full bg-[#2B422F] flex items-center justify-center">
                <ShareNetwork size={24} weight="regular" className="text-[#E6D4C4]" />
              </span>
            </div>

            <div className="mt-5 text-center">
              <span className="label-eyebrow">Spread the word</span>
              <h2 className="font-serif-display text-2xl text-[#1A2421] mt-2 tracking-tight">
                Share Tally
              </h2>
              <p className="text-sm text-[#5C6B62] mt-2">
                Send a friend a link they can tap. No typing, no Safari headaches.
              </p>
            </div>

            <div className="mt-6 space-y-2.5">
              {typeof navigator !== "undefined" && navigator.share && (
                <button
                  onClick={handleNativeShare}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-[#2B422F] text-[#F9F8F6] hover:bg-[#1F3022] transition-colors"
                  data-testid="share-native"
                >
                  <ShareNetwork size={20} weight="bold" />
                  <span className="text-sm font-semibold">Share via…</span>
                </button>
              )}

              <button
                onClick={handleSMS}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-[#E6E3D8] hover:bg-[#F0EFE9] transition-colors"
                data-testid="share-sms"
              >
                <ChatCircle size={20} className="text-[#5BAE7B]" weight="fill" />
                <span className="text-sm text-[#1A2421]">Text message</span>
              </button>

              <button
                onClick={handleEmail}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-[#E6E3D8] hover:bg-[#F0EFE9] transition-colors"
                data-testid="share-email"
              >
                <EnvelopeSimple size={20} className="text-[#F4A26C]" weight="fill" />
                <span className="text-sm text-[#1A2421]">Email</span>
              </button>

              <button
                onClick={handleCopy}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-[#E6E3D8] hover:bg-[#F0EFE9] transition-colors"
                data-testid="share-copy"
              >
                {copied ? (
                  <CheckCircle size={20} className="text-[#5BAE7B]" weight="fill" />
                ) : (
                  <Copy size={20} className="text-[#5C6B62]" weight="bold" />
                )}
                <span className="text-sm text-[#1A2421]">{copied ? "Copied!" : "Copy link"}</span>
              </button>
            </div>

            <div className="mt-5 p-3 rounded-xl bg-white border border-[#E6E3D8]">
              <div className="flex items-center gap-2">
                <LinkIcon size={14} className="text-[#5C6B62]" />
                <a
                  href={APP_URL}
                  className="text-xs text-[#1A2421] font-mono break-all hover:underline"
                  data-testid="share-link-display"
                >
                  {APP_URL}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
